// src/services/FilesService.js
import API from "./Config";

const normalizeIdValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const match = trimmed.match(/ObjectId\s*\(["']?([a-fA-F0-9]{24})["']?\)/);
    if (match) return match[1];
    return trimmed;
  }
  if (typeof value === "object" && value._id) {
    return normalizeIdValue(value._id);
  }
  return String(value);
};

const normalizeFile = (item = {}, candidateIds) => {
  const fallbackName = item.name || "Archivo";
  const candidateSet = new Set(
    Array.isArray(candidateIds) ? candidateIds.filter(Boolean) : []
  );

  const resolveDisplayName = () => {
    if (
      item.displayName &&
      typeof item.displayName === "string" &&
      item.displayName.trim()
    ) {
      return item.displayName.trim();
    }
    if (item.alias && typeof item.alias === "string" && item.alias.trim()) {
      return item.alias.trim();
    }
    return fallbackName;
  };

  const resolvePatientFlag = () => {
    return normalizeIdValue(item.patient ?? item.patientId);
  };

  const resolveGeneralFlag = () => {
    if (typeof item.general === "boolean") {
      return item.general;
    }
    if (typeof item.rawGeneral === "boolean") {
      return item.rawGeneral;
    }
    return false;
  };

  const buildExcludedList = () => {
    if (!Array.isArray(item.excludedPatients)) return [];
    return item.excludedPatients
      .map((p) => normalizeIdValue(p))
      .filter(Boolean);
  };

  const general = resolveGeneralFlag();
  const patient = resolvePatientFlag();
  const excludedPatients = buildExcludedList();
  const excludedForPatient =
    general &&
    excludedPatients.length > 0 &&
    Array.from(candidateSet).some((id) => excludedPatients.includes(id));

  return {
    id: item.id || item._id || `${fallbackName}-${Math.random()}`,
    name: fallbackName,
    alias: item.alias ?? null,
    displayName: resolveDisplayName(),
    size: Number(item.size) || 0,
    contentType: item.contentType || "application/octet-stream",
    url: item.url || "",
    general,
    patient,
    patientId: patient,
    createdAt: item.createdAt || null,
    updatedAt: item.updatedAt || null,
    owner: item.owner ?? null,
    bucket: item.bucket ?? null,
    path: item.path ?? null,
    tags: Array.isArray(item.tags) ? item.tags : [],
    excludedPatients,
    excludedForPatient,
  };
};

const fetchFiles = async (currentPatientId, currentUser) => {
  const candidateSet = new Set();
  const pushCandidate = (value) => {
    const normalized = normalizeIdValue(value);
    if (normalized) candidateSet.add(normalized);
  };

  pushCandidate(currentPatientId);
  if (currentUser) {
    pushCandidate(currentUser._id);
    pushCandidate(currentUser.id);
    if (currentUser.paciente) {
      pushCandidate(currentUser.paciente._id);
      pushCandidate(currentUser.paciente.usuario);
    }
    pushCandidate(currentUser.usuario);
  }

  const { data } = await API.get("/files/mine", {
    params: {
      skip: 0,
      limit: 50,
      patientId: currentPatientId,
    },
  });

  const candidateList = Array.from(candidateSet);

  const items = Array.isArray(data?.items)
    ? data.items.map((item) => normalizeFile(item, candidateList))
    : [];

  const generalItemsRaw = Array.isArray(data?.generalItems)
    ? data.generalItems
    : Array.isArray(data?.generales)
    ? data.generales
    : [];

  const generalFiles = generalItemsRaw.map((item) =>
    normalizeFile(item, candidateList)
  );

  const combined = [...items, ...generalFiles];
  const personales = combined.filter((file) => !file.general);
  const generales = combined.filter(
    (file) => file.general && !file.excludedForPatient
  );
  const todos = [...personales, ...generales];

  return {
    personales,
    generales,
    total: typeof data?.total === "number" ? data.total : todos.length,
    todos,
  };
};

export default { fetchFiles };
